import { useMemo } from "react";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import { filterManufacturingInventoryMaterials } from "@/lib/manufacturing/filterManufacturingInventoryMaterials";
import type {
  ActiveFilter,
  MsdsStatusFilter,
  PriceFilter,
  QtyFilter,
  RecipeUsageFilter,
} from "./ManufacturingInventorySearchCard";

export function useManufacturingInventoryMaterialFilters(
  materials: readonly MfgMaterial[],
  search: string,
  category: string,
  activeFilter: ActiveFilter,
  msdsStatusFilter: MsdsStatusFilter = "all",
  recipeUsageFilter: RecipeUsageFilter = "all",
  usedInRecipeIds: ReadonlySet<string> = new Set(),
  usedInRecipeIdsReady = true,
  qtyFilter: QtyFilter = "all",
  priceFilter: PriceFilter = "all",
) {
  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const m of materials) set.add((m.category || "Uncategorized").trim());
    return ["all", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [materials]);

  const existingUnits = useMemo(() => {
    const set = new Set<string>();
    for (const m of materials) {
      const unit = m.unit?.trim();
      if (unit) set.add(unit);
    }
    return Array.from(set);
  }, [materials]);

  const existingShippingUnits = useMemo(() => {
    const set = new Set<string>();
    for (const m of materials) {
      const shippingUnit = m.shipping_unit?.trim();
      if (shippingUnit) set.add(shippingUnit);
    }
    return Array.from(set);
  }, [materials]);

  const filtered = useMemo(
    () =>
      filterManufacturingInventoryMaterials(materials, {
        search,
        category,
        activeFilter,
        msdsStatusFilter,
        recipeUsageFilter,
        qtyFilter,
        priceFilter,
        usedInRecipeIds,
        usedInRecipeIdsReady,
      }),
    [
      materials,
      category,
      search,
      activeFilter,
      msdsStatusFilter,
      recipeUsageFilter,
      qtyFilter,
      priceFilter,
      usedInRecipeIds,
      usedInRecipeIdsReady,
    ],
  );

  return { categories, existingUnits, existingShippingUnits, filtered };
}
